import { useTranslation } from 'react-i18next';
import svgPaths from "../imports/svg-rrrm6v7dze";

interface PageHeaderProps {
  onHelpClick?: () => void;
  currentQuestion?: number;
  totalQuestions?: number;
}

export function PageHeader({ onHelpClick, currentQuestion, totalQuestions }: PageHeaderProps = {}) {
  const { t } = useTranslation('application');
  const logoUrl = t('header.logoUrl');
  const logoImageSrc = t('header.logoImageSrc');
  
  // Show progress tracking if both currentQuestion and totalQuestions are provided
  const showProgress = currentQuestion !== undefined && totalQuestions !== undefined;
  const progress = showProgress ? (currentQuestion! / totalQuestions!) * 100 : 0;
  const questionProgressText = showProgress 
    ? t('header.questionProgress', { current: currentQuestion!, total: totalQuestions! })
    : '';
  
  // Height: 80px with progress bar, 64px without
  const headerHeight = showProgress ? 'h-[80px]' : 'h-[64px]';
  
  return (
    <header role="banner" className={`relative w-full ${headerHeight} flex-shrink-0`}>
      {/* Dark background bar */}
      <div className="absolute bg-[#131313] h-[64px] left-0 right-0 top-0" />
      
      {/* End the Cage Age Logo */}
      {logoUrl ? (
        <a 
          rel="noopener noreferrer"
          className="absolute h-[39px] left-[48px] max-[900px]:left-2 top-[12px] w-[98px]"
        >
          <img 
            alt={t('header.logoAlt')} 
            className="absolute inset-0 max-w-none object-50%-50% pointer-events-none size-full" 
            src={logoImageSrc} 
          />
        </a>
      ) : (
        <div className="absolute h-[39px] left-[48px] max-[900px]:left-2 top-[12px] w-[98px]">
          <img 
            alt={t('header.logoAlt')} 
            className="absolute inset-0 max-w-none object-50%-50% pointer-events-none size-full" 
            src={logoImageSrc} 
          />
        </div>
      )}
      
      {/* Need Help Section */}
      <button 
        onClick={onHelpClick}
        className="absolute flex items-center gap-2 right-[48px] max-[900px]:right-4 top-[20px] hover:opacity-80 transition-opacity cursor-pointer"
        disabled={!onHelpClick}
        aria-label={t('header.needHelp')}
      >
        <p className="hidden md:block font-['Manrope',_sans-serif] font-bold text-[16px] text-white">
          {t('header.needHelp')}
        </p>
        <div className="size-[24px]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
            <g clipPath="url(#clip0_9_98)">
              <g id="Vector"></g>
              <path d={svgPaths.pe593d70} fill="#ECECEC" />
            </g>
            <defs>
              <clipPath id="clip0_9_98">
                <rect fill="white" height="24" width="24" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </button>
      
      {/* Question Counter - only show if progress tracking is enabled */}
      {showProgress && (
        <p className="absolute font-['Sofia_Sans_Extra_Condensed',_sans-serif] font-bold h-[30px] leading-none left-1/2 text-[#ececec] text-[26px] text-center top-[calc(50%-22px)] translate-x-[-50%] w-[312px] uppercase">
          {questionProgressText}
        </p>
      )}
      
      {/* Progress Bar Container - only show if progress tracking is enabled */}
      {showProgress && (
        <div 
          role="progressbar"
          aria-label="Consultation progress"
          aria-valuenow={currentQuestion}
          aria-valuemin={1}
          aria-valuemax={totalQuestions}
          aria-valuetext={questionProgressText}
          className="absolute h-[16px] left-0 right-0 top-[64px]"
        >
          {/* Gray background */}
          <div className="absolute bg-[#ececec] h-full w-full" />
          {/* Pink progress */}
          <div 
            className="absolute bg-[#FFAB97] h-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </header>
  );
}
